/**
 * Staff Assignment Modal Component
 * Modal for assigning staff members to reports (proposals)
 */

import React, { useState, useMemo } from 'react';
import { Modal, Button, Card, Badge, LoadingSpinner, EmptyState } from '@tracekit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { assignStaffToReport, getReportStaffAssignments } from '../services/rfpApi';
import { getAPIClient, listEntities } from '@tracekit/core';
import {
  User,
  Search,
  X,
  CheckCircle,
  AlertTriangle,
  Clock,
  Award,
  Building2,
  Filter,
} from 'lucide-react';

interface StaffMember {
  id: string;
  name: string;
  department?: string;
  designations?: string[];
  expertise_areas?: string[];
  availability?: string;
  location?: {
    city?: string;
    province?: string;
    office?: string;
  };
}

interface StaffAssignmentModalProps {
  reportId: string;
  reportName?: string;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function StaffAssignmentModal({
  reportId,
  reportName,
  projectId,
  isOpen,
  onClose,
  onSuccess,
}: StaffAssignmentModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [role, setRole] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterDesignation, setFilterDesignation] = useState('all');
  
  const queryClient = useQueryClient();

  // Fetch staff members
  const { data: staffData, isLoading: staffLoading } = useQuery<{
    staff_members: StaffMember[];
  }>({
    queryKey: ['staff-members', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const entities = await listEntities(projectId, 'staff_member', 1000);
      const staffMembers = entities
        .filter(e => (e.data as any)?.name) // Only include entities with names
        .map(e => ({
          id: e.id,
          name: (e.data as any)?.name || 'Unknown',
          department: (e.data as any)?.department,
          designations: (e.data as any)?.designations || [],
          expertise_areas: (e.data as any)?.expertise_areas || [],
          availability: (e.data as any)?.availability,
          location: (e.data as any)?.location,
        }));
      return { staff_members: staffMembers };
    },
    enabled: isOpen && !!projectId,
  });

  // Fetch existing assignments to show who's already assigned
  const { data: existingAssignments } = useQuery({
    queryKey: ['report-staff-assignments', reportId],
    queryFn: () => getReportStaffAssignments(reportId),
    enabled: isOpen && !!reportId,
  });

  const assignedStaffIds = useMemo(() => {
    return new Set(existingAssignments?.assignments.map(a => a.staff_member_id) || []);
  }, [existingAssignments]);

  // Filter and search staff
  const filteredStaff = useMemo(() => {
    if (!staffData?.staff_members) return [];
    
    return staffData.staff_members.filter((staff) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = staff.name.toLowerCase().includes(query);
        const matchesDept = staff.department?.toLowerCase().includes(query);
        const matchesDesignation = staff.designations?.some(d => d.toLowerCase().includes(query));
        if (!matchesName && !matchesDept && !matchesDesignation) return false;
      }
      
      // Department filter
      if (filterDepartment !== 'all' && staff.department !== filterDepartment) return false;
      
      // Designation filter
      if (filterDesignation !== 'all') {
        const designations = staff.designations || [];
        if (!designations.includes(filterDesignation)) return false;
      }
      
      // Exclude already assigned staff
      if (assignedStaffIds.has(staff.id)) return false;
      
      return true;
    });
  }, [staffData, searchQuery, filterDepartment, filterDesignation, assignedStaffIds]);

  // Get unique departments and designations for filters
  const departments = useMemo(() => {
    if (!staffData?.staff_members) return [];
    const depts = new Set(staffData.staff_members.map(s => s.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [staffData]);

  const designations = useMemo(() => {
    if (!staffData?.staff_members) return [];
    const desigs = new Set(staffData.staff_members.flatMap(s => s.designations || []));
    return Array.from(desigs).sort();
  }, [staffData]);

  // Assignment mutation
  const assignMutation = useMutation({
    mutationFn: (staffId: string) => assignStaffToReport(reportId, staffId, {
      role: role || undefined,
      assignedHoursPerWeek: hoursPerWeek ? Number(hoursPerWeek) : undefined,
      notes: notes || undefined,
    }),
    onSuccess: () => {
      // Invalidate all assignment-related queries to refresh counts everywhere
      queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
      onSuccess?.();
      handleClose();
    },
  });

  const handleClose = () => {
    setSelectedStaffId(null);
    setRole('');
    setHoursPerWeek('');
    setNotes('');
    setSearchQuery('');
    setFilterDepartment('all');
    setFilterDesignation('all');
    onClose();
  };

  const handleAssign = () => {
    if (!selectedStaffId) return;
    assignMutation.mutate(selectedStaffId);
  };

  const selectedStaff = filteredStaff.find(s => s.id === selectedStaffId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Assign Staff to Proposal${reportName ? `: ${reportName}` : ''}`}
      size="lg"
    >
      <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        {/* Filters */}
        <div className="space-y-4 flex-shrink-0 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, department..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Department</label>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800"
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Designation</label>
              <select
                value={filterDesignation}
                onChange={(e) => setFilterDesignation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800"
              >
                <option value="all">All Designations</option>
                {designations.map(desig => (
                  <option key={desig} value={desig}>{desig}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Staff List - Scrollable */}
        <div className="flex-shrink-0 mb-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
          {staffLoading ? (
            <div className="p-8 text-center">
              <LoadingSpinner />
              <p className="mt-2 text-gray-600">Loading staff members...</p>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="p-8 text-center">
              <EmptyState
                icon={<User className="w-12 h-12" />}
                title="No Staff Found"
                description={searchQuery || filterDepartment !== 'all' || filterDesignation !== 'all' 
                  ? "Try adjusting your filters"
                  : "No available staff members found"}
              />
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredStaff.map((staff) => {
                const isSelected = selectedStaffId === staff.id;
                return (
                  <button
                    key={staff.id}
                    onClick={() => setSelectedStaffId(staff.id)}
                    className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {staff.name}
                          </span>
                          {isSelected && (
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-400">
                          {staff.department && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {staff.department}
                            </span>
                          )}
                          {staff.designations && staff.designations.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Award className="w-3 h-3" />
                              {staff.designations.join(', ')}
                            </span>
                          )}
                          {staff.availability && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {staff.availability}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          </div>
        </div>

        {/* Assignment Details */}
        {selectedStaff && (
          <div className="flex-shrink-0 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold mb-4">Assignment Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g., Project Manager, Technical Lead, Writer"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Hours per Week</label>
                  <input
                    type="number"
                    value={hoursPerWeek}
                    onChange={(e) => setHoursPerWeek(e.target.value ? Number(e.target.value) : '')}
                    placeholder="e.g., 10"
                    min="0"
                    max="40"
                    step="0.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes about this assignment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800"
                  />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!selectedStaffId || assignMutation.isPending}
          >
            {assignMutation.isPending ? (
              <>
                <LoadingSpinner className="w-4 h-4 mr-2" />
                Assigning...
              </>
            ) : (
              'Assign Staff'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
